/**
 * API Configuration - Smart URL detection for local and remote access
 */

/**
 * Get the API base URL based on current environment
 */
export const getApiBaseUrl = (): string => {
  // Return default for SSR/build time (will be replaced at runtime in browser)
  if (typeof window === 'undefined') {
    return 'http://localhost/api/v1';
  }
  
  // ALWAYS use dynamic detection based on hostname
  // This ensures ngrok mobile access works correctly
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  console.log('Detecting API URL from hostname:', hostname, 'protocol:', protocol);
  
  // If accessing via ngrok (mobile/remote testing)
  if (hostname.includes('ngrok-free.dev') || hostname.includes('ngrok.io')) {
    const ngrokUrl = `${protocol}//${hostname}/api/v1`;
    console.log('Detected ngrok, using URL:', ngrokUrl);
    return ngrokUrl;
  }
  
  // Check if localhost or 127.0.0.1
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    console.log('Detected localhost, using http://localhost/api/v1');
    return 'http://localhost/api/v1';
  }
  
  // For any other domain, use the same host
  const detectedUrl = `${protocol}//${hostname}/api/v1`;
  console.log('Using detected URL:', detectedUrl);
  return detectedUrl;
};

/**
 * Get the WebSocket URL based on current environment
 */
export const getWebSocketUrl = (): string => {
  // Return default for SSR/build time
  if (typeof window === 'undefined') {
    return 'ws://localhost:6001';
  }
  
  // ALWAYS use dynamic detection based on hostname
  const hostname = window.location.hostname;
  const isSecure = window.location.protocol === 'https:';
  const wsProtocol = isSecure ? 'wss:' : 'ws:';
  
  // If accessing via ngrok
  if (hostname.includes('ngrok-free.dev') || hostname.includes('ngrok.io')) {
    return `${wsProtocol}//${hostname}:6001`;
  }
  
  // Default to localhost
  return 'ws://localhost:6001';
};

// Export timeout constant
export const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT || '120000', 10);

// Export as constant for backwards compatibility
// NOTE: This returns a default value that works for most local development
// For production/ngrok, the api.ts interceptor dynamically calls getApiBaseUrl()
export const API_BASE_URL = 'http://localhost/api/v1';

// Log configuration for debugging on first call (browser only)
let configLogged = false;
export const logApiConfig = () => {
  // Only run in browser, not during SSR or webpack build
  // Check for both window and document to ensure we're in a real browser
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  
  if (!configLogged && process.env.NODE_ENV === 'development') {
    configLogged = true;
    console.log('API Configuration:', JSON.stringify({
      apiUrl: getApiBaseUrl(),
      wsUrl: getWebSocketUrl(),
      hostname: window.location.hostname,
      protocol: window.location.protocol,
    }, null, 2));
  }
};

// Don't log immediately - let it be called when the app actually starts
// The log will happen on first API call or when explicitly called
